import { useOutlookStore, useResourceStore, useSettingStore, useThemeStore } from "@shared/stores";
import React, { useCallback, useState, useRef, useEffect } from "react";
import HStack from "@components/layout/HStack";
import TitleSetting from "@components/ui/TitleSetting";
import Divider from "@components/ui/Divider";
import VStack from "@components/layout/VStack";
import SelectLocationForm from "@features/search/components/SelectLocationForm";
import Select from "@components/ui/Select";
import Accordion from "@components/ui/Accordion";
import { settingLabels } from "@shared/constants/setting";
import {
  DefaultResourceTypeEntity,
  OrganizerSettingEntity,
} from "@features/settings/services/entities/organizerSettingEntity";
import { useCreateOrganizerSetting } from "@features/settings/services/useCreateOrganizerSetting";
import { useGlobalContext } from "@shared/contexts/GlobalContext";
import { useAppConfigStore } from "@shared/stores/appConfigStore";
import { ResourceTypeEntity } from "@features/search/services/entities/resourceTypeEntity";
import { useTranslation } from "react-i18next";
import { LocationIcon } from "@/pages/taskpane/components/icons/IconCustom";
import { useSearchParams } from "react-router-dom";
import { filterResourceDefault } from "@/pages/taskpane/shared/constants/appConstants";

const LocationSetting = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const [searchParams] = useSearchParams();
  const targetResourceType = searchParams.get("targetResourceType");

  const [isOpen, setIsOpen] = useState(targetResourceType ? true : false);

  const header = (
    <HStack justifyContent="between" className="w-full" onClick={() => setIsOpen(!isOpen)}>
      <TitleSetting
        icon={<LocationIcon isDarkMode={isDarkMode} size={20} />}
        label={t(settingLabels.defaultLocation)}
      />
    </HStack>
  );

  return (
    <Accordion header={header} isOpen={isOpen} onToggleOpen={() => setIsOpen(!isOpen)}>
      {isOpen ? <LocationSettingContent /> : null}
    </Accordion>
  );
};

export default LocationSetting;

// CONTENT

const LocationSettingContent = () => {
  const { t } = useTranslation();
  const { setIsLoadingOverlay } = useGlobalContext();
  // const showToast = useToastStore((state) => state.showToast);
  const { organizer } = useOutlookStore();
  const { defaultLocations, updateDefaultResourceTypes } = useSettingStore();
  const { resourceTypes, locations } = useAppConfigStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetResourceType = searchParams.get("targetResourceType");
  const targetRef = useRef<HTMLDivElement | null>(null);
  const { filterResource, setFilterResource } = useResourceStore();

  const { mutate: updateDefaultLocationsMutation } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        //  Nếu update location của resourceType đang filter trong search thì update lại location trong filter
        const currentResourceType = filterResource?.resourceType;
        if (currentResourceType) {
          const oldResourceType = defaultLocations.defaultResourceTypes.find((rt) => rt.type === currentResourceType);
          const newResourceType = data.defaultLocations.defaultResourceTypes.find(
            (rt) => rt.type === currentResourceType
          );

          if (oldResourceType && newResourceType) {
            const oldLocationIds = oldResourceType.locationIds.split(",").filter(Boolean).map(Number);
            const newLocationIds = newResourceType.locationIds.split(",").filter(Boolean).map(Number);

            const oldLocationIdsSet = new Set(oldLocationIds);
            const newLocationIdsSet = new Set(newLocationIds);

            const hasChanged =
              oldLocationIdsSet.size !== newLocationIdsSet.size ||
              [...oldLocationIdsSet].some((id) => !newLocationIdsSet.has(id)) ||
              [...newLocationIdsSet].some((id) => !oldLocationIdsSet.has(id));

            if (hasChanged) {
              setFilterResource({
                ...(filterResource || filterResourceDefault),
                locations: newLocationIds,
              });
            }
          }
        }
        
        updateDefaultResourceTypes(data.defaultLocations.defaultResourceTypes);
        // showToast(t("Location changed"), true);
      },
      onError: () => {
        // showToast(t("Location change failed"), false);
      },
      onSettled: () => {
        setIsLoadingOverlay(false);
      },
    },
  });

  const handleApply = useCallback(
    (selectedIds: number[], type: string) => {
      if (!organizer) return;
      if (!selectedIds?.length) {
        // showToast(t("Please select at least one location"), false);
        return;
      }
      setIsLoadingOverlay(true);

      // Clear targetResourceType from URL if it exists
      if (targetResourceType) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("targetResourceType");
        setSearchParams(newSearchParams, { replace: true });
      }

      const existingTypeIndex = defaultLocations.defaultResourceTypes.findIndex((rt) => rt.type === type);
      let resourceTypesNew;
      if (existingTypeIndex !== -1) {
        resourceTypesNew = [...defaultLocations.defaultResourceTypes];
        resourceTypesNew[existingTypeIndex] = { type, locationIds: selectedIds.join(",") };
      } else {
        resourceTypesNew = [...defaultLocations.defaultResourceTypes, { type, locationIds: selectedIds.join(",") }];
      }

      updateDefaultLocationsMutation({
        organizerEmail: organizer,
        defaultLocations: {
          defaultLocationIds: defaultLocations.defaultLocationIds,
          defaultResourceTypes: resourceTypesNew,
        },
      });
    },
    [organizer, defaultLocations, updateDefaultLocationsMutation, targetResourceType, searchParams, setSearchParams]
  );

  const createLocationFormComponent = useCallback(
    (resourceType: DefaultResourceTypeEntity) => {
      // Return a component function (not memoized to always get fresh resourceType)
      return (props: { onClose?: () => void; maxHeight?: number }) => {
        const locationSelected = resourceType.locationIds.split(",").map((i) => parseInt(i));

        return (
          <SelectLocationForm
            {...props}
            locationSelected={locationSelected}
            onApply={(selectedIds) => handleApply(selectedIds, resourceType.type)}
            isShowClearButton={false}
          />
        );
      };
    },
    [handleApply]
  );

  const handleGetPlaceholder = useCallback(
    (resourceType: DefaultResourceTypeEntity) => {
      const locationSelected = resourceType.locationIds.split(",").map((i) => parseInt(i));
      let placeholder = "";
      if (locationSelected?.length && locationSelected.length > 0) {
        locations.forEach((i) => {
          if (locationSelected.includes(i.id) && i.parentNodeId) {
            placeholder = placeholder + (placeholder ? ", " : "") + i.caption;
          }
        });
      }
      return placeholder;
    },
    [locations]
  );

  // Scroll to target resource type when it's specified in URL
  useEffect(() => {
    if (targetResourceType && targetRef.current) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        targetRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [targetResourceType, defaultLocations]);

  return (
    <div className="w-full">
      <Divider className="my-3" />

      <div className="w-full">
        <HStack className="w-full text-sm font-semibold">
          <p className="w-[30%] min-w-[120px] pl-3">{t("Resource types")}</p>
          <p className="flex-1">{t("Location")}</p>
        </HStack>

        <Divider className="my-3" />

        <VStack className="w-full gap-6">
          {defaultLocations?.defaultResourceTypes?.length
            ? defaultLocations.defaultResourceTypes.map((i) => {
                const uniqueKey = `${i.type}-${i.locationIds}`;
                const isTarget = targetResourceType === i.type;

                return (
                  <HStack key={uniqueKey} ref={isTarget ? targetRef : null} className="w-full" justifyContent="between">
                    <div className="w-[30%] min-w-[120px] pl-3 text-sm">
                      {resourceTypes?.find((rt: ResourceTypeEntity) => rt.type === i.type)?.displayName || ""}
                    </div>

                    <div className="w-[60%] pr-1">
                      <Select
                        key={uniqueKey}
                        defaultOpen={isTarget}
                        placeholder={handleGetPlaceholder(i) || t("Where")}
                        tooltipMessage={handleGetPlaceholder(i) || ""}
                        className="w-full h-[32px] text-xs bg-white dark:bg-darkBackgroundColor"
                        Children={createLocationFormComponent(i)}
                        maxHeightDropdown={300}
                        isHighlightSelected={Boolean(i.locationIds)}
                        alignTo="right"
                        tooltipMaxLines={4}
                      />
                    </div>
                  </HStack>
                );
              })
            : null}
        </VStack>
      </div>
    </div>
  );
};
